import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { GoogleMaps } from "@/components/dashboard/google-maps";
import { 
  Shield, 
  Globe, 
  AlertTriangle, 
  CheckCircle, 
  Clock,
  ExternalLink,
  Search,
  Filter,
  MapPin,
  TrendingUp,
  Activity,
  Lock,
  Users,
  Database,
  Download
} from "lucide-react";

export default function Domains() {
  const [searchTerm, setSearchTerm] = useState("");
  const [filterType, setFilterType] = useState("all");
  const [selectedDomain, setSelectedDomain] = useState(null);
  const [location] = useLocation();

  // Parse search query from URL parameters
  useEffect(() => {
    const urlParams = new URLSearchParams(location.split('?')[1] || '');
    const searchQuery = urlParams.get('search');
    if (searchQuery) {
      setSearchTerm(searchQuery);
    }
  }, [location]);

  const { data: domains = [], isLoading } = useQuery({
    queryKey: ["/api/domains"],
    refetchInterval: 60000, // Refresh every minute
  }) as { data: Array<{
    id: string;
    domain: string;
    category: string;
    type: string;
    securityRating: string;
    lastScan: string;
    scriptsDetected: number;
    vulnerabilities: number;
    status: string;
  }> | undefined, isLoading: boolean };

  // Use only authentic domain data from API - no fallback mock data
  const domainsData = Array.isArray(domains) ? domains : [];

  // Generate world map data based on authentic domain analysis
  const generateWorldMapData = () => {
    // For now, create a simple representation based on real domain data
    const countryData = [
      { country: "United States", domains: domainsData.length, threats: domainsData.filter(d => d.securityRating === 'C' || d.securityRating === 'D' || d.securityRating === 'F').length, x: 240, y: 150, code: "US" }
    ];
    
    // Add more geographical intelligence based on actual domain origins when available
    return countryData;
  };
  
  const worldMapData = generateWorldMapData();

  const getRatingColor = (rating: string) => {
    switch (rating) {
      case "A": return "bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-400";
      case "B": return "bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-400";
      case "C": return "bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-400";
      case "D": return "bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-400";
      case "F": return "bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-400";
      default: return "bg-gray-100 dark:bg-gray-900/30 text-gray-800 dark:text-gray-400";
    }
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case "payment": return "bg-purple-100 text-purple-800 border-purple-200";
      case "analytics": return "bg-blue-100 text-blue-800 border-blue-200";
      case "marketing": return "bg-purple-100 text-purple-800 border-purple-200";
      case "cdn": return "bg-orange-100 text-orange-800 border-orange-200";
      case "main": return "bg-purple-100 text-purple-800 border-purple-200";
      default: return "bg-slate-100 text-slate-800 border-slate-200";
    }
  };

  const getCategoryLabel = (category: string) => {
    switch (category) {
      case "cdn": return "Content Delivery Network";
      case "consent": return "Consent Management";
      case "marketing": return "Marketing Scripts";
      case "payment": return "Payment Processing";
      case "video": return "Video Platform";
      case "analytics": return "Analytics";
      case "main": return "Primary Domain";
      default: return category;
    }
  };

  // Filter authentic domains data only
  const filteredDomains = domainsData.filter(domain => {
    // If no search term, show all domains (filtered by type only)
    if (!searchTerm.trim()) {
      const matchesFilter = filterType === "all" || 
                            (filterType === "owned" && domain.type === "owned") ||
                            (filterType === "third-party" && domain.type === "third-party");
      return matchesFilter;
    }

    // Search matching - make it more precise
    const searchLower = searchTerm.toLowerCase().trim();
    const domainLower = domain.domain.toLowerCase();
    
    // Exact match, contains match, or starts with match
    const matchesSearch = 
      domainLower === searchLower ||
      domainLower.includes(searchLower) ||
      domainLower.startsWith(searchLower.replace('www.', '')) ||
      domain.category.toLowerCase().includes(searchLower) ||
      (domain.scanUrl && domain.scanUrl.toLowerCase().includes(searchLower));
    
    const matchesFilter = filterType === "all" || 
                          (filterType === "owned" && domain.type === "owned") ||
                          (filterType === "third-party" && domain.type === "third-party");
    
    return matchesSearch && matchesFilter;
  });

  const ownedDomains = domainsData.filter(d => d.type === "owned");
  const thirdPartyDomains = domainsData.filter(d => d.type === "third-party");

  const summary = {
    total: domainsData.length,
    owned: ownedDomains.length,
    thirdParty: thirdPartyDomains.length,
    secure: domainsData.filter(d => d.securityRating === 'A' || d.securityRating === 'B').length
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">
            Domain Security Monitor
          </h1>
          <p className="text-slate-600 dark:text-slate-400">
            Comprehensive domain monitoring with real-time threat detection and geographic analysis
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => {
              const link = document.createElement('a');
              link.href = '/api/export/domains';
              link.download = `domains-${new Date().toISOString().split('T')[0]}.csv`;
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
            }}
          >
            <Download className="h-4 w-4 mr-2" />
            Export Report
          </Button>
          <Badge variant="secondary" className="text-xs">
            <Globe className="h-3 w-3 mr-1" />
            {summary.total} Domains
          </Badge>
        </div>
      </div>

      {/* Summary Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Globe className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <div className="text-xl font-bold text-slate-900 dark:text-white">
                  {summary.total}
                </div>
                <div className="text-xs text-slate-600">Monitored Domains</div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Lock className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <div className="text-xl font-bold text-purple-600">
                  {summary.owned}
                </div>
                <div className="text-xs text-slate-600">Primary Domains</div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-100 rounded-lg">
                <ExternalLink className="h-5 w-5 text-orange-600" />
              </div>
              <div>
                <div className="text-xl font-bold text-orange-600">
                  {summary.thirdParty}
                </div>
                <div className="text-xs text-slate-600">External Services</div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Shield className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <div className="text-xl font-bold text-purple-600">
                  {summary.secure}
                </div>
                <div className="text-xs text-slate-600">Security Validated</div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Global Domain Distribution Map */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MapPin className="h-5 w-5 text-purple-600" />
            Global Domain Distribution
          </CardTitle>
          <CardDescription>
            Interactive world map showing domain locations and security threats by region
          </CardDescription>
        </CardHeader>
        <CardContent>
          {/* Real Interactive Google Maps World Domain Distribution */}
          <GoogleMaps 
            threatData={worldMapData.map(location => ({
              lat: location.country === "United States" ? 39.8283 :
                   location.country === "United Kingdom" ? 51.5074 :
                   location.country === "Germany" ? 51.1657 :
                   location.country === "Singapore" ? 1.3521 :
                   location.country === "Canada" ? 56.1304 :
                   location.country === "Australia" ? -25.2744 :
                   location.country === "Japan" ? 36.2048 :
                   location.country === "Brazil" ? -14.2350 : 0,
              lng: location.country === "United States" ? -98.5795 :
                   location.country === "United Kingdom" ? -0.1278 :
                   location.country === "Germany" ? 10.4515 :
                   location.country === "Singapore" ? 103.8198 :
                   location.country === "Canada" ? -106.3468 :
                   location.country === "Australia" ? 133.7751 :
                   location.country === "Japan" ? 138.2529 :
                   location.country === "Brazil" ? -51.9253 : 0,
              region: location.country,
              threats: location.domains,
              severity: location.threats > 2 ? 'high' : location.threats > 0 ? 'medium' : 'low',
              details: [
                `${location.domains} monitored domains`,
                `${location.threats} security threats detected`,
                'Real-time domain monitoring active'
              ]
            }))}
            height="384px"
            className="w-full border border-slate-200 dark:border-slate-700 rounded-lg"
          />
        </CardContent>
      </Card>

      {/* Regional Statistics - Moved below map */}
      <Card className="mt-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Globe className="h-5 w-5 text-purple-600" />
            Regional Domain Statistics
          </CardTitle>
          <CardDescription>
            Domain distribution and security threats by geographic region
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {worldMapData.slice(0, 4).map((location) => (
              <div key={location.code} className="text-center p-4 bg-slate-100 dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700">
                <div className="text-2xl font-bold text-slate-900 dark:text-white mb-1">
                  {location.domains}
                </div>
                <div className="text-sm font-medium text-slate-600 dark:text-slate-400 mb-1">
                  {location.country}
                </div>
                <div className="text-xs text-slate-500">
                  {location.threats} threat{location.threats !== 1 ? 's' : ''}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Search and Filter */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            placeholder="Search domains..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        <div className="flex items-center gap-2">
          <Filter className="h-4 w-4 text-slate-500" />
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
          >
            <option value="all">All Types</option>
            <option value="owned">Owned</option>
            <option value="third-party">Third-Party</option>
          </select>
        </div>
      </div>

      {/* Domain List */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Database className="h-5 w-5 text-blue-600" />
            Domain Inventory ({filteredDomains.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
              <span className="ml-3 text-slate-600">Loading domains...</span>
            </div>
          ) : filteredDomains.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <Globe className="h-12 w-12 mx-auto mb-4 text-slate-300" />
              <div className="text-lg font-medium mb-2">No Domains Found</div>
              <div className="text-sm">
                {searchTerm ? (
                  <>No domains match "{searchTerm}". Try a different search term.</>
                ) : filterType !== "all" ? (
                  <>No {filterType} domains found. Try changing the filter.</>
                ) : (
                  <>No domains have been detected in your recent scans.</>
                )}
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredDomains.map((domain) => (
                <div
                  key={domain.id}
                  className="flex items-center justify-between p-4 border rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors"
                >
                  <div className="flex items-center gap-4 flex-1">
                    <div className="text-2xl">
                      {domain.type === "owned" ? "🏠" : "🌐"}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-medium text-sm">{domain.domain}</span>
                        <Badge className={`text-xs border ${getRatingColor(domain.securityRating)}`}>
                          {domain.securityRating}
                        </Badge>
                        <Badge className={`text-xs border ${getCategoryColor(domain.category)}`}>
                          {getCategoryLabel(domain.category)}
                        </Badge>
                        {domain.type === "third-party" && (
                          <Badge variant="outline" className="text-xs">
                            <ExternalLink className="h-3 w-3 mr-1" />
                            External
                          </Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-4 text-xs text-slate-500">
                        <span className="flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          Last scanned: {new Date(domain.lastScanned).toLocaleDateString()}
                        </span>
                        {domain.scanUrl && (
                          <span className="flex items-center gap-1">
                            <Activity className="h-3 w-3" />
                            Found on: {new URL(domain.scanUrl).hostname}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <Button variant="ghost" size="sm">
                    <ExternalLink className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}